import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { authClient } from "../lib/auth-client";
import { Button } from "./components/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/card";
import { Toast } from "./components/toast";
import { AuthPanel } from "./features/auth/auth-panel";
import { AdminView } from "./features/admin/admin-view";
import { ChartOfAccountsView } from "./features/accounts/chart-of-accounts-view";
import { BankTransactionsImport } from "./features/bank-transactions/bank-transactions-import";
import { BankTransactionsView } from "./features/bank-transactions/bank-transactions-view";
import { BookingsView } from "./features/bookings/bookings-view";
import { NotificationsView } from "./features/notifications/notifications-view";
import { JournalEntriesView } from "./features/entries/entries-view";
import { ConsumptionView } from "./features/consumption/consumption-view";
import { MeterReadingsView } from "./features/meter-readings/meter-readings-view";
import { DashboardView } from "./features/dashboard/dashboard-view";
import { SettingsView } from "./features/settings/settings-view";
import { PendingInvites } from "./features/houses/pending-invites";
import { ReceivablesView } from "./features/receivables/receivables-view";
import { BalanceSheetView } from "./features/reports/balance-sheet-view";
import { BudgetWorksheetView } from "./features/reports/budget-worksheet-view";
import { ProfitLossView } from "./features/reports/profit-loss-view";
import { StatisticsView } from "./features/reports/statistics-view";
import { useLocation } from "./hooks/use-location";
import { fetchJson } from "./lib/api";
import { buildNavGroups, buildOverviewNavItems, logoUrl } from "./lib/constants";
import type { House, HouseInvite, NavIcon, WorkerJobLog } from "./lib/types";
import { I18nProvider, useI18n } from "./i18n/i18n";
import { defaultLocale, type Locale } from "../i18n/translations";

const navIcons = {
  dashboard: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z" />
    </svg>
  ),
  bookings: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 3v3M16 3v3M4 9h16M6 6h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
    </svg>
  ),
  notifications: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3a6 6 0 0 0-6 6v3.5L4 15v1h16v-1l-2-2.5V9a6 6 0 0 0-6-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </svg>
  ),
  statistics: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 20h16M7 16v-6M12 16V8M17 16v-3" />
    </svg>
  ),
  "meter-readings": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12a8 8 0 1 0 16 0a8 8 0 1 0-16 0" />
      <path d="M12 8v5l3 3" />
    </svg>
  ),
  consumption: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 20h16" />
      <path d="M6 16l4-4 3 3 5-6" />
      <path d="M18 9h-3V6" />
    </svg>
  ),
  receivables: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 7h10M7 11h10M7 15h6M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    </svg>
  ),
  "profit-loss": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 18l6-6 4 4 6-8M20 8h-4V4" />
    </svg>
  ),
  budget: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18M3 18h18M6 3v18M12 3v18M18 3v18" />
    </svg>
  ),
  "balance-sheet": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4v16M4 7h6v5H4zM14 7h6v5h-6zM6 17h4v3H6zM14 17h4v3h-4z" />
    </svg>
  ),
  "journal-entries": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 6h10M7 10h10M7 14h6M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    </svg>
  ),
  "chart-of-accounts": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h10M4 12h16M4 18h12M18 6h2M8 12h2M14 18h2" />
    </svg>
  ),
  "bank-transactions": (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 10h18M5 10v9M9 10v9M15 10v9M19 10v9M3 10l9-6 9 6M4 19h16" />
    </svg>
  ),
  settings: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      <path d="M19.4 15a1.8 1.8 0 0 0 .36 1.97l.03.03a2 2 0 1 1-2.83 2.83l-.03-.03a1.8 1.8 0 0 0-1.97-.36 1.8 1.8 0 0 0-1.09 1.64V21a2 2 0 1 1-4 0v-.04a1.8 1.8 0 0 0-1.09-1.64 1.8 1.8 0 0 0-1.97.36l-.03.03a2 2 0 1 1-2.83-2.83l.03-.03a1.8 1.8 0 0 0 .36-1.97 1.8 1.8 0 0 0-1.64-1.09H3a2 2 0 1 1 0-4h.04a1.8 1.8 0 0 0 1.64-1.09 1.8 1.8 0 0 0-.36-1.97l-.03-.03a2 2 0 1 1 2.83-2.83l.03.03a1.8 1.8 0 0 0 1.97.36h.02A1.8 1.8 0 0 0 9 3.04V3a2 2 0 1 1 4 0v.04c0 .72.43 1.37 1.09 1.64h.02a1.8 1.8 0 0 0 1.97-.36l.03-.03a2 2 0 1 1 2.83 2.83l-.03.03a1.8 1.8 0 0 0-.36 1.97v.02c.27.66.92 1.09 1.64 1.09H21a2 2 0 1 1 0 4h-.04a1.8 1.8 0 0 0-1.64 1.09z" />
    </svg>
  ),
  admin: (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3l7 3v6c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z" />
      <path d="M9.5 12l2 2 4-4" />
    </svg>
  ),
} as const satisfies Record<NavIcon, React.ReactNode>;

const AppShell = () => {
  const { data: session, isPending } = authClient.useSession();
  const { t, setLocale } = useI18n();
  const { pathname, search, navigate, setSearch } = useLocation();
  const [houses, setHouses] = useState<House[]>([]);
  const [invites, setInvites] = useState<HouseInvite[]>([]);
  const [logs, setLogs] = useState<WorkerJobLog[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string } | null>(null);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const isRefreshingLogs = useRef(false);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const selectedHouseId = Number.parseInt(searchParams.get("house") ?? "", 10);
  const isSetupOnly = houses.length === 0;
  const resolvedHouseId = Number.isNaN(selectedHouseId) ? null : selectedHouseId;
  const selectedHouse = useMemo(
    () => houses.find((house) => house.id === resolvedHouseId) ?? null,
    [houses, resolvedHouseId],
  );
  const selectedHouseRole = selectedHouse?.role ?? "full";
  const navGroups = useMemo(() => buildNavGroups(t), [t]);
  const environmentLabel = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }
    const hostname = window.location.hostname.toLowerCase();
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.endsWith(".local")
    ) {
      return t("environment.dev", "Dev");
    }
    if (
      hostname.startsWith("staging.") ||
      hostname.includes(".staging.") ||
      hostname.includes("-staging")
    ) {
      return t("environment.staging", "Staging");
    }
    return null;
  }, [t]);
  const visibleNavGroups = useMemo(() => {
    if (isSetupOnly) {
      const setupGroups = navGroups.filter(
        (group) => group.id === "settings" || group.id === "admin",
      );
      return isOwner ? setupGroups : setupGroups.filter((group) => group.id !== "admin");
    }
    return isOwner ? navGroups : navGroups.filter((group) => group.id !== "admin");
  }, [isOwner, isSetupOnly]);
  const overviewItems = useMemo(() => buildOverviewNavItems(visibleNavGroups), [visibleNavGroups]);

  const handleHouseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("house", event.target.value);
    setSearch(`?${nextParams.toString()}`);
  };

  const loadData = async () => {
    setIsLoadingData(true);
    setDataError(null);
    try {
      const [housesResponse, inviteResponse, logsResponse] = await Promise.all([
        fetchJson<{ houses: House[] }>("/api/houses"),
        fetchJson<{ invites: HouseInvite[] }>("/api/invites"),
        fetchJson<{ logs: WorkerJobLog[] }>("/api/import-logs"),
      ]);
      setHouses(housesResponse.houses);
      setInvites(inviteResponse.invites);
      setLogs(logsResponse.logs);
    } catch (error) {
      setDataError(
        error instanceof Error ? error.message : t("errors.loadData", "Failed to load data"),
      );
    } finally {
      setIsLoadingData(false);
      setHasLoadedData(true);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await fetchJson<{ locale: Locale }>("/api/profile");
      setLocale(response.locale);
    } catch {
      setLocale(defaultLocale);
    }
  };

  const loadOwnerStatus = async () => {
    try {
      const data = await fetchJson<{ isOwner: boolean }>("/api/admin/owner");
      setIsOwner(Boolean(data.isOwner));
    } catch {
      setIsOwner(false);
    }
  };

  const handleInviteAccepted = async (invite: HouseInvite) => {
    await loadData();
    const nextParams = new URLSearchParams(search);
    nextParams.set("house", String(invite.houseId));
    navigate("/bookings", `?${nextParams.toString()}`);
  };

  const refreshImportLogs = useCallback(async () => {
    if (isRefreshingLogs.current) {
      return;
    }
    isRefreshingLogs.current = true;
    try {
      const logsResponse = await fetchJson<{ logs: WorkerJobLog[] }>("/api/import-logs");
      setLogs(logsResponse.logs);
    } catch {
      // Ignore background refresh errors.
    } finally {
      isRefreshingLogs.current = false;
    }
  }, []);

  useEffect(() => {
    if (session) {
      void loadData();
      void loadProfile();
    }
  }, [session]);

  useEffect(() => {
    if (session) {
      void loadOwnerStatus();
      return;
    }
    setIsOwner(false);
  }, [session]);

  useEffect(() => {
    if (!houses.length) {
      return;
    }
    const hasSelected = houses.some((house) => house.id === selectedHouseId);
    if (!hasSelected) {
      const nextParams = new URLSearchParams(search);
      nextParams.set("house", String(houses[0].id));
      setSearch(`?${nextParams.toString()}`, { replace: true });
    }
  }, [houses, search, selectedHouseId, setSearch]);

  useEffect(() => {
    if (
      !hasLoadedData ||
      !isSetupOnly ||
      pathname === "/settings" ||
      (isOwner && pathname === "/admin")
    ) {
      return;
    }
    navigate("/settings", search);
  }, [hasLoadedData, isSetupOnly, pathname, navigate, search, isOwner]);

  useEffect(() => {
    if (!hasLoadedData || isSetupOnly || pathname !== "/") {
      return;
    }
    navigate("/dashboard", search);
  }, [hasLoadedData, isSetupOnly, pathname, navigate, search]);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timeoutId = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  useEffect(() => {
    setIsNavOpen(false);
  }, [pathname]);

  if (isPending) {
    return <div className="p-6 text-muted">{t("auth.loadingSession", "Loading session...")}</div>;
  }

  if (!session) {
    return <AuthPanel />;
  }

  const navCards = visibleNavGroups.map((group) => (
    <Card key={group.id}>
      <CardContent className="space-y-2 p-3">
        <p className="px-3 text-xs font-semibold uppercase tracking-wide text-muted">
          {group.label}
        </p>
        <div className="space-y-1">
          {group.items.map((page) => (
            <Button
              key={page.path}
              variant={page.path === pathname ? "default" : "ghost"}
              className="w-full justify-start gap-2 brand-menu"
              onClick={() => {
                navigate(page.path, search);
                setIsNavOpen(false);
              }}
            >
              {navIcons[page.icon]}
              <span>{page.label}</span>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  ));

  return (
    <div className="min-h-screen bg-brand">
      <header className="app-shell-header border-b brand-border brand-surface">
        <div className="app-shell-header-inner mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="app-shell-menu md:hidden"
              onClick={() => setIsNavOpen(true)}
            >
              {t("navigation.menu", "Menu")}
            </Button>
            <img src={logoUrl} alt="Hytteflow logo" className="h-10 w-10" loading="lazy" />
            <div className="flex items-center gap-2">
              <p className="text-lg brand-title text-ink">Hytteflow</p>
              {environmentLabel ? (
                <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink">
                  {environmentLabel}
                </span>
              ) : null}
            </div>
          </div>
          <div className="app-shell-header-actions flex w-full flex-wrap items-center gap-3 md:w-auto md:justify-end">
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <select
                id="house-select"
                className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus sm:min-w-[220px]"
                value={selectedHouseId ? String(selectedHouseId) : ""}
                onChange={handleHouseChange}
                disabled={!houses.length}
                aria-label={t("house.selectLabel", "House")}
              >
                <option value="" disabled>
                  {t("house.selectPlaceholder", "Select house")}
                </option>
                {houses.map((house) => (
                  <option key={house.id} value={house.id}>
                    {house.name}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-xs text-muted sm:text-sm">{session.user?.email}</span>
            <Button variant="outline" size="sm" onClick={() => authClient.signOut()}>
              {t("auth.signOut", "Sign out")}
            </Button>
          </div>
        </div>
      </header>
      {isNavOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsNavOpen(false)}
            aria-label={t("navigation.close", "Close navigation")}
          />
          <div className="relative h-full w-72 max-w-[80vw] overflow-y-auto bg-brand p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-ink">
                {t("navigation.title", "Navigation")}
              </p>
              <button
                type="button"
                className="rounded-md border border-transparent p-2 text-ink transition hover:bg-slate-100"
                onClick={() => setIsNavOpen(false)}
                aria-label={t("navigation.close", "Close navigation")}
              >
                ×
              </button>
            </div>
            <div className="mt-3 space-y-2">{navCards}</div>
          </div>
        </div>
      ) : null}
      <div className="app-shell-layout mx-auto flex max-w-6xl flex-col gap-6 px-4 py-6 md:flex-row md:px-6">
        <nav className="app-shell-nav hidden w-64 shrink-0 space-y-2 md:block">{navCards}</nav>
        <main className="app-shell-main min-w-0 flex-1 space-y-6">
          {toast ? <Toast message={toast.message} onClose={() => setToast(null)} /> : null}
          {dataError ? <p className="text-sm text-error">{dataError}</p> : null}
          {isLoadingData ? (
            <p className="text-sm text-muted">{t("app.loadingData", "Loading data...")}</p>
          ) : null}
          <PendingInvites invites={invites} onAccepted={handleInviteAccepted} />
          {isSetupOnly ? (
            <SettingsView
              houses={houses}
              logs={logs}
              onRefresh={loadData}
              onRefreshLogs={refreshImportLogs}
              selectedHouseId={resolvedHouseId}
            />
          ) : pathname === "/dashboard" ? (
            <DashboardView
              houseId={resolvedHouseId}
              logs={logs}
              search={search}
              navigate={navigate}
            />
          ) : pathname === "/settings" ? (
            <SettingsView
              houses={houses}
              logs={logs}
              onRefresh={loadData}
              onRefreshLogs={refreshImportLogs}
              selectedHouseId={resolvedHouseId}
            />
          ) : pathname === "/admin" ? (
            <AdminView onHouseAccessClaimed={loadData} />
          ) : pathname === "/bookings" ? (
            <BookingsView houseId={resolvedHouseId} search={search} setSearch={setSearch} />
          ) : pathname === "/notifications" ? (
            <NotificationsView houseId={resolvedHouseId} />
          ) : pathname === "/journal-entries" ? (
            <JournalEntriesView
              houseId={resolvedHouseId}
              search={search}
              setSearch={setSearch}
              houseRole={selectedHouseRole}
            />
          ) : pathname === "/receivables" ? (
            <ReceivablesView houseId={resolvedHouseId} search={search} setSearch={setSearch} />
          ) : pathname === "/profit-loss" ? (
            <ProfitLossView houseId={resolvedHouseId} search={search} setSearch={setSearch} />
          ) : pathname === "/budget" ? (
            <BudgetWorksheetView
              houseId={resolvedHouseId}
              search={search}
              setSearch={setSearch}
              houseRole={selectedHouseRole}
            />
          ) : pathname === "/statistics" ? (
            <StatisticsView houseId={resolvedHouseId} search={search} setSearch={setSearch} />
          ) : pathname === "/balance-sheet" ? (
            <BalanceSheetView houseId={resolvedHouseId} search={search} setSearch={setSearch} />
          ) : pathname === "/chart-of-accounts" ? (
            <ChartOfAccountsView
              houseId={resolvedHouseId}
              search={search}
              setSearch={setSearch}
              navigate={navigate}
            />
          ) : pathname === "/meter-readings" ? (
            <MeterReadingsView houseId={resolvedHouseId} />
          ) : pathname === "/consumption" ? (
            <ConsumptionView houseId={resolvedHouseId} />
          ) : pathname === "/bank-transactions" ? (
            <BankTransactionsView
              houseId={resolvedHouseId}
              search={search}
              setSearch={setSearch}
              navigate={navigate}
              houseRole={selectedHouseRole}
            />
          ) : pathname === "/bank-transactions/import" ? (
            <BankTransactionsImport
              houseId={resolvedHouseId}
              search={search}
              setSearch={setSearch}
              navigate={navigate}
              onImported={(message) => setToast({ message })}
              houseRole={selectedHouseRole}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t("app.welcome", "Welcome")}</CardTitle>
                <p className="text-sm text-muted">
                  {t("app.selectPage", "Select a page to get started.")}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  {overviewItems.map((page) => (
                    <Card key={page.path} className="border-dashed">
                      <CardHeader>
                        <CardTitle>{page.label}</CardTitle>
                        <p className="text-sm text-muted">{page.description}</p>
                      </CardHeader>
                      <CardContent>
                        <Button variant="outline" onClick={() => navigate(page.path, search)}>
                          {t("app.openPage", "Open page")}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
};

const rootElement = document.getElementById("app");
if (rootElement) {
  const root = createRoot(rootElement);
  root.render(
    <I18nProvider>
      <AppShell />
    </I18nProvider>,
  );
}
